import React from 'react';
import { useTranslation } from 'react-i18next';
import { Timer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Agent } from '@/types/Agents';
import { getUsageMetrics } from '@/utils/subscriptionUtils';
import { getIntlLocale } from '@/utils/supportedLanguages';

interface SubscriptionUsageCardProps {
  subscription: Agent['subscription'];
  /** When false, renders content only without the outer Card wrapper */
  wrapped?: boolean;
  showPlanName?: boolean;
  className?: string;
}

export const SubscriptionUsageCard: React.FC<SubscriptionUsageCardProps> = ({
  subscription,
  wrapped = true,
  showPlanName = true,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const {
    usageSeconds,
    includedMinutes,
    overageSeconds,
    estimatedOverageAmount,
    overagePricePerMinute,
    billingExempt,
    hasUsage,
    usagePercent,
  } = getUsageMetrics(subscription);

  if (!hasUsage) return null;

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };
  const currency = subscription?.currency ?? 'EUR';
  const formatMoney = (amount: number) =>
    new Intl.NumberFormat(getIntlLocale(i18n.resolvedLanguage), {
      style: 'currency',
      currency,
    }).format(amount);

  const content = (
    <div className={wrapped ? 'space-y-4' : 'space-y-3'}>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Timer className="h-4 w-4" />
            {t(
              'agent.dashboard.usage.duration.translation',
              'Billable duration'
            )}
          </div>
          <p className="font-semibold text-foreground">
            {t('agent.dashboard.usage.duration_value.translation', {
              used: formatDuration(usageSeconds),
              limit: includedMinutes ?? 0,
              defaultValue: '{{used}} / {{limit}} min included',
            })}
          </p>
        </div>
        {usagePercent != null && (
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-brand transition-all"
              style={{ width: `${usagePercent}%` }}
            />
          </div>
        )}
      </div>

      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div className="rounded-md bg-muted/50 p-3">
          <p className="text-muted-foreground">
            {t('agent.dashboard.usage.overage.translation', 'Overage')}
          </p>
          <p className="font-semibold text-foreground">
            {overageSeconds > 0
              ? formatDuration(overageSeconds)
              : t('agent.dashboard.usage.no_overage.translation', 'None')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('agent.dashboard.usage.overage_rate.translation', {
              rate: formatMoney(overagePricePerMinute),
              defaultValue: '{{rate}} per minute, charged by exact second',
            })}
          </p>
        </div>
        <div className="rounded-md bg-muted/50 p-3">
          <p className="text-muted-foreground">
            {t(
              'agent.dashboard.usage.estimated_overage.translation',
              'Estimated overage'
            )}
          </p>
          <p className="font-semibold text-foreground">
            {billingExempt
              ? t(
                  'agent.dashboard.usage.billing_exempt.translation',
                  'Not billed'
                )
              : formatMoney(estimatedOverageAmount)}
          </p>
        </div>
      </div>
    </div>
  );

  if (!wrapped) {
    return <div className={className}>{content}</div>;
  }

  return (
    <Card className={`bg-card ${className ?? ''}`}>
      <CardHeader className="pb-2">
        <CardTitle>
          {t('agent.dashboard.usage.title.translation', 'Plan Usage')}
        </CardTitle>
        {showPlanName && subscription?.plan_name && (
          <p className="text-sm text-muted-foreground">
            {subscription.plan_name}
          </p>
        )}
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
};
